import * as React from 'react'
import {
  meterTrackVariants,
  meterFillVariants,
  type MeterState,
  type MeterSize,
} from './Meter.variants'
import { cn } from '../../utils/cn'

export type { MeterState, MeterSize }

export type MeterProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'role'> & {
  /** Mastery ratio in [0, 1]. Out-of-range values are clamped. */
  value: number
  /** Semantic bucket — maps to the DS color scale (green/gold/red/neutral). */
  state?: MeterState
  /** Data below the reliability cutoff: reduced opacity + hatch overlay.
   *  Never render a low-answered-count value as if it were solid. */
  provisional?: boolean
  /** Accessible label (also used for aria-label). */
  label?: string
  size?: MeterSize
}

// Diagonal hatch overlay for `provisional`: a pure textural rgba stripe
// pattern (same spirit as tokens.css `--paper-noise`), not a new brand color —
// the semantic color scale itself still comes entirely from meterFillVariants.
// Exported so other DS-adjacent components needing the same "provisional data"
// treatment (e.g. the Padronanza "Spettro" strip in @arianna/reader-ui) render
// an identical texture instead of hand-rolling their own hatch pattern.
export const METER_HATCH_BACKGROUND_IMAGE =
  'repeating-linear-gradient(135deg, rgba(0,0,0,0.22) 0, rgba(0,0,0,0.22) 3px, transparent 3px, transparent 6px)'

export default function Meter({
  value,
  state = 'insufficient',
  provisional = false,
  label,
  size = 'md',
  className,
  style,
  ...rest
}: MeterProps) {
  const clamped = Math.min(1, Math.max(0, value))
  const percent = Math.round(clamped * 100)

  return (
    <div
      role="meter"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuetext={`${percent}%`}
      aria-label={label}
      className={cn(meterTrackVariants({ size }), className)}
      style={style}
      {...rest}
    >
      <div
        className={cn(meterFillVariants({ state, provisional }))}
        style={{
          width: `${percent}%`,
          ...(provisional ? { backgroundImage: METER_HATCH_BACKGROUND_IMAGE } : {}),
        }}
      />
    </div>
  )
}
